import { storage } from "../Firebase/firebase-config";
import { ref as storageRef, uploadBytes, getDownloadURL, listAll } from "firebase/storage";
import { v4 as uuidv4 } from "uuid"; // npm install uuid

class FileStorage {
  /**
   * Uploads a document to Firebase Storage
   * @param {File} file - The document file
   * @param {string} chatId - Chat ID
   * @param {string} userId - User ID of sender
   * @returns {Promise<Object>} - Resolves with file metadata
   */
  static async uploadFile(file, chatId, userId) {
    try {
      this.validateFile(file);

      // Generate unique file ID
      const fileId = `${chatId}_${userId}_${Date.now()}_${uuidv4()}`;

      // Reference in Firebase Storage
      const storageReference = storageRef(storage, `chatDocuments/${userId}/${chatId}/${fileId}_${file.name}`);

      // Upload the file
      await uploadBytes(storageReference, file);

      // Get the download URL
      const fileUrl = await getDownloadURL(storageReference);

      return {
        id: fileId,
        url: fileUrl,
        name: file.name,
        type: file.type,
        size: file.size,
        uploadedBy: userId,
        uploadedAt: Date.now(),
        chatId: chatId,
      };
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  }

  /**
   * Get all files for a specific chat (directly from Storage)
   * @param {string} userId
   * @param {string} chatId
   * @returns {Promise<Array>}
   */
  static async getChatFiles(userId, chatId) {
    try {
      const folderRef = storageRef(storage, `chatDocuments/${userId}/${chatId}`);
      const result = await listAll(folderRef);

      // Get download URLs for all files
      const files = await Promise.all(
        result.items.map(async (itemRef) => {
          const url = await getDownloadURL(itemRef);
          const [fileId, ...rest] = itemRef.name.split("_");
          return {
            id: fileId,
            name: rest.join("_"), // original filename preserved
            url,
          };
        })
      );

      return files;
    } catch (error) {
      console.error("Error fetching chat files:", error);
      return [];
    }
  }

  /**
   * Validate file type and size
   * @param {File} file
   */
static validateFile(file) {
  const maxSize = 5 * 1024 * 1024; // 5MB
  const allowedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "image/png",
    "image/jpeg",
    "image/jpg",
  ];

  if (file.size > maxSize) {
    throw new Error("File size must be less than 5MB");
  }

  if (!allowedTypes.includes(file.type)) {
    throw new Error("Only PDF, DOC, DOCX, PNG, JPG, and JPEG files are allowed");
  }

  return true;
}

}

export default FileStorage;
